import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  MAX_PARTICIPANTS_EXTENDED_ONLY,
  MAX_PARTICIPANTS_STANDARD_TIER,
} from '../../utils/eventParticipantLimits';
import './MaxParticipantsSelect.css';

/**
 * Lista miejsc zamiast <select> — w rozwinięciu wiersze 10–20 mają badge „Premium”
 * (natywny select nie pozwala na HTML wewnątrz listy).
 */
function MaxParticipantsSelect({
  id,
  value,
  onChange,
  disabled = false,
  extendedMaxParticipants = false,
  tierLoaded = true,
  participantsLabel,
  sectionStandardLabel,
  premiumBadgeLabel,
  listboxAriaLabel,
}) {
  const listboxId = `${id}-listbox`;
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [menuBox, setMenuBox] = useState({ top: 0, left: 0, width: 0 });

  const close = useCallback(() => setOpen(false), []);

  const updateMenuPosition = useCallback(() => {
    const el = rootRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setMenuBox({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updateMenuPosition();
  }, [open, updateMenuPosition]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocMouseDown = (e) => {
      const t = e.target;
      const inTrigger = rootRef.current?.contains(t);
      const inMenu = menuRef.current?.contains(t);
      if (!inTrigger && !inMenu) {
        close();
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', updateMenuPosition, true);
    window.addEventListener('resize', updateMenuPosition);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
    };
  }, [open, close, updateMenuPosition]);

  const pick = (n, canPick) => {
    if (!canPick || disabled) return;
    onChange(String(n));
    close();
  };

  const displayValue = `${value} ${participantsLabel}`.trim();

  return (
    <div ref={rootRef} className={`max-participants-select${open ? ' max-participants-select--open' : ''}`}>
      <button
        type="button"
        id={id}
        className="max-participants-select-trigger"
        disabled={disabled || !tierLoaded}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        onClick={() => !disabled && tierLoaded && setOpen((o) => !o)}
      >
        <span className="max-participants-select-trigger-value">{displayValue}</span>
        <span className="max-participants-select-trigger-chevron" aria-hidden />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            id={listboxId}
            className="max-participants-select-dropdown max-participants-select-dropdown--portal"
            role="listbox"
            aria-label={listboxAriaLabel}
            style={{
              position: 'fixed',
              top: menuBox.top,
              left: menuBox.left,
              width: menuBox.width,
              zIndex: 4000,
            }}
          >
            <div className="max-participants-select-section" role="presentation">
              <div className="max-participants-select-section-label">{sectionStandardLabel}</div>
              {MAX_PARTICIPANTS_STANDARD_TIER.map((n) => {
                const selected = String(n) === String(value);
                return (
                  <button
                    key={n}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={`max-participants-select-option${selected ? ' is-selected' : ''}`}
                    onClick={() => pick(n, true)}
                  >
                    <span>
                      {n} {participantsLabel}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="max-participants-select-section" role="presentation">
              {MAX_PARTICIPANTS_EXTENDED_ONLY.map((n) => {
                const selected = String(n) === String(value);
                const locked = !extendedMaxParticipants;
                const rowClass = `max-participants-select-option max-participants-select-option--extended${
                  selected ? ' is-selected' : ''
                }${locked ? ' is-locked' : ''}`;
                const inner = (
                  <>
                    <span className="max-participants-select-option-main">
                      {n} {participantsLabel}
                    </span>
                    <span className="max-participants-premium-badge">{premiumBadgeLabel}</span>
                  </>
                );
                if (locked) {
                  return (
                    <div
                      key={n}
                      role="option"
                      aria-selected={selected}
                      aria-disabled="true"
                      className={rowClass}
                    >
                      {inner}
                    </div>
                  );
                }
                return (
                  <button
                    key={n}
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={rowClass}
                    onClick={() => pick(n, true)}
                  >
                    {inner}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

export default MaxParticipantsSelect;
